import { ExternalReturnHubType } from "@redotech/account-common/address";
import type { PendingSettingsChangeForReview } from "@redotech/admin-sdk/rpc/schema/settings-approval/get-pending-settings-changes";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import ArrowDownIcon from "@redotech/redo-icons/arrow-down.svg";
import EyeIcon from "@redotech/redo-icons/eye.svg";
import { returnsCenterIdFormat } from "@redotech/redo-warehouse-id";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoDrawer } from "@redotech/redo-web/arbiter-components/modal/redo-drawer";
import { Divider } from "@redotech/redo-web/divider";
import { Flex } from "@redotech/redo-web/flex";
import { Icon } from "@redotech/redo-web/icon";
import { Text } from "@redotech/redo-web/text";
import { memo, ReactNode, useCallback, useEffect, useState } from "react";
import { RedoAdminRpcClientContext } from "../../app/redo-admin-rpc-client-provider";
import * as styles from "./requested-changes-panel.module.css";

type CenterNameMap = ReadonlyMap<string, string>;

const NO_VALUE = "redo.NoValue";

const HUB_DISPLAY_NAMES: Record<ExternalReturnHubType, string> = {
  [ExternalReturnHubType.RETURNBEAR]: "ReturnBear",
  [ExternalReturnHubType.BLUEYONDER]: "Blue Yonder",
  [ExternalReturnHubType.SHIPMONK]: "ShipMonk",
  [ExternalReturnHubType.SHIPHERO]: "ShipHero",
  [ExternalReturnHubType.RYDER]: "Ryder",
  [ExternalReturnHubType.DEPOSCO]: "Deposco",
  [ExternalReturnHubType.NETSUITE]: "NetSuite",
  [ExternalReturnHubType.MINTSOFT]: "Mintsoft",
};

const BILLING_MODE_LABELS: Record<string, string> = {
  percentage: "Revenue share",
  fixed: "Fixed fee",
};

const MARKUP_MODE_LABELS: Record<string, string> = {
  percentage: "Percentage",
  fixed: "Fixed amount",
};

function formatDollars(v: unknown): string {
  const n = Number(v);
  return isNaN(n) ? String(v) : `$${n.toFixed(2)}`;
}

function formatPercent(v: unknown): string {
  const n = Number(v);
  return isNaN(n) ? String(v) : `${n}%`;
}

function formatBillingConfig(obj: Record<string, unknown>): string {
  const mode = BILLING_MODE_LABELS[String(obj.billingMode)] ?? obj.billingMode;
  if (obj.billingMode === "percentage") {
    return `${mode} — ${formatPercent(obj.revSharePercentage)}`;
  }
  return `${mode} — ${formatDollars(obj.fixedFeeAmount)}`;
}

function formatShippingMarkup(obj: Record<string, unknown>): string {
  const mode = MARKUP_MODE_LABELS[String(obj.markupMode)] ?? obj.markupMode;
  if (obj.markupMode === "percentage") {
    return `${mode} — ${formatPercent(obj.markupPercentage)}`;
  }
  return `${mode} — ${formatDollars(obj.markupAmount)}`;
}

function formatReturnsCenter(
  obj: Record<string, unknown>,
  centerNames?: CenterNameMap,
): string {
  const parts: string[] = [];
  if (obj.returnsCenterId) {
    const id = String(obj.returnsCenterId);
    const name = centerNames?.get(id);
    parts.push(name ?? `Center ${id}`);
  }
  if (obj.refulfillment !== undefined) {
    parts.push(`Refulfillment: ${obj.refulfillment ? "On" : "Off"}`);
  }
  if (obj.consolidationScheduleDays != null) {
    parts.push(`Schedule: every ${obj.consolidationScheduleDays} days`);
  }
  if (obj.processingFee != null && Number(obj.processingFee) > 0) {
    parts.push(`Processing fee: ${formatDollars(obj.processingFee)}`);
  }
  if (typeof obj.shipToAddress === "object" && obj.shipToAddress !== null) {
    const addr = obj.shipToAddress as Record<string, unknown>;
    const addrParts = [addr.name, addr.city, addr.state, addr.country]
      .filter(Boolean)
      .map(String);
    if (addrParts.length > 0) {
      parts.push(`Ship to: ${addrParts.join(", ")}`);
    }
  }
  return parts.join("\n");
}

function formatReceivingLabel(obj: Record<string, unknown>): string {
  const enabled = obj.enabled ? "Enabled" : "Disabled";
  if (obj.encodedField) {
    return `${enabled} — ${String(obj.encodedField)}`;
  }
  return enabled;
}

function looksLikeHubMappings(obj: Record<string, unknown>): boolean {
  const entries = Object.entries(obj);
  if (entries.length === 0) {
    return false;
  }
  return entries.every(
    ([, val]) => typeof val === "object" && val !== null && "provider" in val,
  );
}

function formatHubMappings(
  obj: Record<string, unknown>,
  centerNames?: CenterNameMap,
): string {
  const lines: string[] = [];
  for (const [centerId, val] of Object.entries(obj)) {
    if (typeof val !== "object" || val === null) {
      continue;
    }
    const hub = val as Record<string, unknown>;
    const centerName = centerNames?.get(centerId) ?? `Center ${centerId}`;
    const providerName =
      HUB_DISPLAY_NAMES[String(hub.provider) as ExternalReturnHubType] ??
      hub.provider ??
      "None";
    const locPart = hub.locationId ? ` (${hub.locationId})` : "";
    lines.push(`${centerName}: ${providerName}${locPart}`);
  }
  return lines.length > 0 ? lines.join("\n") : "(empty)";
}

function formatObject(
  obj: Record<string, unknown>,
  centerNames?: CenterNameMap,
): string {
  if ("billingMode" in obj && "revSharePercentage" in obj) {
    return formatBillingConfig(obj);
  }
  if ("markupMode" in obj) {
    return formatShippingMarkup(obj);
  }
  if ("returnsCenterId" in obj) {
    return formatReturnsCenter(obj, centerNames);
  }
  if ("enabled" in obj && "encodedField" in obj) {
    return formatReceivingLabel(obj);
  }
  if (looksLikeHubMappings(obj)) {
    return formatHubMappings(obj, centerNames);
  }
  const lines: string[] = [];
  for (const [key, val] of Object.entries(obj)) {
    if (typeof val === "object" && val !== null) {
      continue;
    }
    lines.push(`${key}: ${val == null ? "\u2014" : String(val)}`);
  }
  return lines.join("\n");
}

function renderValue(value: unknown, centerNames?: CenterNameMap): ReactNode {
  if (value == null || value === NO_VALUE) {
    return (
      <Text fontSize="xs" textColor="tertiary">
        (not set)
      </Text>
    );
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return (
        <Text fontSize="xs" textColor="tertiary">
          (empty)
        </Text>
      );
    }
    return (
      <Flex dir="column" gap="sm">
        {value.map((item, i) => {
          const display =
            typeof item === "object" && item !== null
              ? formatObject(item as Record<string, unknown>, centerNames)
              : String(item);
          return (
            <Flex align="flex-start" gap="xs" key={i}>
              <Text fontSize="xs" textColor="tertiary">
                {i + 1}.
              </Text>
              <Text fontSize="xs" textColor="primary" whiteSpace="pre-wrap">
                {display}
              </Text>
            </Flex>
          );
        })}
      </Flex>
    );
  }

  if (typeof value === "object") {
    const obj = value as Record<string, unknown>;
    if (Object.keys(obj).length === 0) {
      return (
        <Text fontSize="xs" textColor="tertiary">
          (empty)
        </Text>
      );
    }
    return (
      <Text fontSize="xs" textColor="primary" whiteSpace="pre-wrap">
        {formatObject(obj, centerNames)}
      </Text>
    );
  }

  if (typeof value === "boolean") {
    return (
      <Text fontSize="xs" fontWeight="medium" textColor="primary">
        {value ? "Enabled" : "Disabled"}
      </Text>
    );
  }

  return (
    <Text fontSize="xs" textColor="primary">
      {String(value)}
    </Text>
  );
}

const FIELD_LABELS: Record<string, string> = {
  billingMode: "Billing mode",
  revSharePercentage: "Rev share %",
  fixedFeeAmount: "Fixed fee amount",
  markupMode: "Markup mode",
  markupPercentage: "Markup percentage",
  markupAmount: "Markup amount",
  enabled: "Enabled",
  encodedField: "Encoded field",
  defaultFulfillmentLocation: "Default fulfillment location",
  externalLocationId: "Default fulfillment location",
  teamId: "Default location team",
  allowSplitShipments: "Allow split shipments",
  routingRules: "Routing rules",
  fulfillmentTags: "Fulfillment tags",
  markAsPrinted: "Mark as printed",
  sharedInventory: "Shared inventory",
};

function changeDisplayLabel(change: {
  label: string | null;
  path: string;
}): string {
  const base = change.label ?? change.path;
  if (!change.label) {
    return base;
  }
  const pathParts = change.path.split(".");
  const lastSegment = pathParts[pathParts.length - 1] ?? "";
  const fieldLabel = FIELD_LABELS[lastSegment];
  if (fieldLabel) {
    return `${base} — ${fieldLabel}`;
  }
  return base;
}

function ValueBlock({
  label,
  variant,
  children,
}: {
  label: string;
  variant: "from" | "to";
  children: ReactNode;
}) {
  return (
    <Flex dir="column" gap="xs">
      <RedoBadge color="gray" size="xs" text={label} />
      <Flex
        className={
          variant === "from" ? styles.valueBlockFrom : styles.valueBlockTo
        }
        dir="column"
      >
        {children}
      </Flex>
    </Flex>
  );
}

export const RequestedChangesPanel = memo(function RequestedChangesPanel({
  teamId,
  onApproved,
  refreshKey,
}: {
  teamId: string;
  onApproved: () => void;
  refreshKey?: number;
}) {
  const rpcClient = useRequiredContext(RedoAdminRpcClientContext);
  const [changes, setChanges] = useState<PendingSettingsChangeForReview[]>([]);
  const [open, setOpen] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [centerNames, setCenterNames] = useState<CenterNameMap>(new Map());

  const load = useHandler(async () => {
    try {
      const result = await rpcClient.getPendingSettingsChanges({ teamId });
      setChanges(result.changes);
    } catch {
      // Non-critical; the panel will remain hidden if changes can't be loaded.
    }
  });

  const loadCenters = useCallback(async () => {
    try {
      const { returnsCenters } = await rpcClient.getReturnsCenters({
        sort: null,
      });
      const map = new Map<string, string>();
      for (const center of returnsCenters) {
        const label = `${center.name}${center.city ? ` — ${center.city}` : ""}${center.state ? `, ${center.state}` : ""}`;
        map.set(center.id, label);
        try {
          const rawId = returnsCenterIdFormat.read(center.id).toString();
          map.set(rawId, label);
        } catch {
          // Ignore; the formatted key is enough.
        }
      }
      setCenterNames(map);
    } catch {
      // Non-critical.
    }
  }, [rpcClient]);

  useEffect(() => {
    void load();
    void loadCenters();
  }, [load, loadCenters, refreshKey]);

  const approve = useHandler(async (id: string) => {
    setBusyId(id);
    try {
      await rpcClient.approveSettingsChanges({ changeIds: [id] });
      await load();
      onApproved();
    } finally {
      setBusyId(null);
    }
  });

  const remove = useHandler(async (id: string) => {
    setBusyId(id);
    try {
      await rpcClient.rejectSettingsChanges({ changeIds: [id] });
      await load();
    } finally {
      setBusyId(null);
    }
  });

  if (changes.length === 0) {
    return null;
  }

  return (
    <>
      <RedoButton
        hierarchy="secondary"
        IconLeading={EyeIcon}
        onClick={() => setOpen(true)}
        size="sm"
        text={`View requested changes (${changes.length})`}
      />
      {open && (
        <RedoDrawer
          onModalCloseRequested={() => setOpen(false)}
          open
          showCloseButton
        >
          <Flex
            className={styles.drawerContent}
            dir="column"
            gap="xl"
            overflowY="auto"
            p="xl"
          >
            <Text fontSize="lg" fontWeight="semibold">
              Requested changes
            </Text>
            <Flex dir="column" gap="xl">
              {changes.map((change) => (
                <Flex dir="column" gap="md" key={change.id}>
                  <Text fontSize="sm" fontWeight="medium">
                    {changeDisplayLabel(change)}
                  </Text>
                  <Flex align="center" dir="column" gap="sm">
                    <ValueBlock label="From" variant="from">
                      {renderValue(change.prev, centerNames)}
                    </ValueBlock>
                    <Icon
                      arbiterIconSvg={ArrowDownIcon}
                      color="ghost"
                      size="compact"
                    />
                    <ValueBlock label="To" variant="to">
                      {renderValue(change.next, centerNames)}
                    </ValueBlock>
                  </Flex>
                  <Text fontSize="xs" textColor="tertiary">
                    Proposed by {change.proposedBy}
                  </Text>
                  {(change.canApprove || change.canDelete) && (
                    <Flex gap="sm">
                      {change.canApprove && (
                        <RedoButton
                          disabled={busyId !== null}
                          hierarchy="primary"
                          onClick={() => approve(change.id)}
                          size="sm"
                          text="Approve"
                        />
                      )}
                      {change.canDelete && (
                        <RedoButton
                          disabled={busyId !== null}
                          hierarchy="secondary"
                          onClick={() => remove(change.id)}
                          size="sm"
                          text="Delete"
                          theme="destructive"
                        />
                      )}
                    </Flex>
                  )}
                  <Divider />
                </Flex>
              ))}
            </Flex>
          </Flex>
        </RedoDrawer>
      )}
    </>
  );
});
